import * as React from 'react'
import {Box, TextField, InputAdornment, FormControl, Select, MenuItem, Button, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {filterBarSx} from '../../../../styles/patterns'
import {FONT, SIZE} from '../../../../styles/foundation/typography'
import {PRIMARY_BLUE, SUBTLE} from '../../../../styles/foundation/colors'
import {SOURCE_LABEL} from './catalogueState'
import {int} from '../format'

const fieldSx = {fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 150}
const SEARCH_DEBOUNCE_MS = 200

// Portalled menu is unscaled — without this its open state paints larger
// than the closed control it belongs to. Shape matches disputes-v2/FilterBar.js.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontSize: SIZE['2xs']}}}}

/**
 * Modelled on disputes-v2/FilterBar.js. One deliberate addition: the search
 * field echoes every keystroke locally but only calls `onChange('q', v)`
 * after a 200ms pause — the mockup this ports has no debounce at all, and
 * without one the table would re-filter all 111 rows on every keystroke.
 */
export default function CatalogueFilters({filters, onChange, onReset, shown, total, subOptions}) {
  const [q, setQ] = React.useState(filters.q)
  const timer = React.useRef(null)

  // Keeps the local echo in sync when the filter is cleared from elsewhere
  // (the "Clear" button here, or the auto-added banner's "Show them" jump).
  React.useEffect(() => { setQ(filters.q) }, [filters.q])

  React.useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const handleSearchChange = (value) => {
    setQ(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => onChange('q', value), SEARCH_DEBOUNCE_MS)
  }

  const hasActiveFilter = !!filters.q || filters.source !== 'all' || filters.sub !== 'all'

  return (
    <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 0}}>
      <TextField
        size='small'
        placeholder='Search SKU, product, category, sub-category'
        value={q}
        onChange={(e) => handleSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
            </InputAdornment>
          ),
        }}
        // aria-label belongs on the native <input> itself (inputProps), not
        // as a bare TextField prop — the latter lands on the outer wrapper
        // and leaves the actual control unlabelled for assistive tech.
        inputProps={{'aria-label': 'Search the catalogue'}}
        sx={{...fieldSx, minWidth: 260}}
      />

      <FormControl size='small' sx={fieldSx}>
        <Select value={filters.source} onChange={(e) => onChange('source', e.target.value)} MenuProps={selectMenuProps}>
          <MenuItem value='all'>All sources</MenuItem>
          {Object.keys(SOURCE_LABEL).map((key) => (
            <MenuItem key={key} value={key}>{SOURCE_LABEL[key]}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size='small' sx={fieldSx}>
        <Select value={filters.sub} onChange={(e) => onChange('sub', e.target.value)} MenuProps={selectMenuProps}>
          <MenuItem value='all'>All sub-categories</MenuItem>
          {subOptions.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActiveFilter && (
        <Button
          size='small'
          onClick={onReset}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: PRIMARY_BLUE}}
        >
          Clear
        </Button>
      )}

      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto', whiteSpace: 'nowrap'}}>
        {int(shown)} of {int(total)} SKUs
      </Typography>
    </Box>
  )
}
